import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { postRequest } from "../../../utils/httpRequest";
import { Errors, formErrors } from "../../../utils/helpers";
import {
  trackEnquiryConversion,
  trackTeamBrochureDownload,
} from "../../../utils/analytics";
import { AxiosError } from "axios";

interface Props {
  recaptchaRef?: any;
  overviewFile: string;
  fileName: string;
  source?: string;
}

interface TeamState {
  saving: boolean;
  requested: boolean;
  contactEmail: string;
  contactName: string;
  teamSize: string;
  captchaError: string;
  errors: Record<string, any>;
}

export const NewTeamForm = (props: Props) => {
  const { recaptchaRef, overviewFile, fileName, source } = props;

  const [teamForm, setTeamForm] = useState<TeamState>({
    saving: false,
    requested: false,
    contactName: "",
    contactEmail: "",
    teamSize: "",
    captchaError: "",
    errors: {},
  });

  const getErrorMessageFor = (type) => {
    return teamForm.errors[type];
  };

  const handleChange = (
    e,
    target: "contactName" | "contactEmail" | "teamSize",
  ) => {
    const value = e.target.value;
    setTeamForm((prev) => ({ ...prev, [target]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // One click, one enquiry, one event: ignore a second press while the
    // first is still with the server.
    if (teamForm.saving) return;
    setTeamForm((prev) => ({ ...prev, saving: true }));

    const recaptchaValue = (recaptchaRef as any).current?.getValue();
    const { contactName, contactEmail, teamSize } = teamForm;
    const teamInfoBody = {
      team_info: {
        contact_name: contactName,
        contact_email: contactEmail,
        team_size_range: teamSize,
        source,
      },
      recaptcha_token: recaptchaValue,
    };

    try {
      await postRequest("/api/team_infos.json", teamInfoBody);
      setTeamForm((prev) => ({ ...prev, saving: false, requested: true }));
      trackEnquiryConversion({
        event: "team_enquiry",
        formLocation: "teams_quote",
        teamSize: teamForm.teamSize,
      });
    } catch (e: unknown) {
      const error = e as AxiosError<{ errors: Errors; captcha_error: string }>;
      setTeamForm((prev) => ({
        ...prev,
        saving: false,
        requested: false,
        errors: formErrors(error.response.data.errors),
        captchaError: error.response.data.captcha_error,
      }));
    }
  };

  const downloadOverview = (e) => {
    e.preventDefault();
    trackTeamBrochureDownload({ formLocation: "teams_quote" });

    fetch(overviewFile, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.setAttribute("download", fileName);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      });
  };

  const contactNameError = getErrorMessageFor("contact_name");
  const contactEmailError = getErrorMessageFor("contact_email");
  const captchaError = teamForm.captchaError;

  return (
    <div className="teams-lead-form-box" aria-label="Get a quote and brochure">
      {teamForm.requested ? (
        <div className="teams-requested">
          <div className="teams-requested-icon" aria-hidden="true">
            ✓
          </div>
          <h3>Thanks! We'll get back to you.</h3>
          <p>
            We've got your details. You can also download the team brochure now.
          </p>
          <a
            onClick={downloadOverview}
            href="#"
            className="teams-btn teams-btn-blue"
          >
            Download Brochure
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="teams-form-group">
            <label htmlFor="team-contact-email">Work Email</label>
            <input
              type="email"
              id="team-contact-email"
              name="email"
              value={teamForm.contactEmail}
              required
              placeholder="you@company.com"
              autoComplete="email"
              onChange={(e) => handleChange(e, "contactEmail")}
            />
            {contactEmailError && (
              <p className="teams-error" role="alert">
                {contactEmailError}
              </p>
            )}
          </div>
          <div className="teams-form-group">
            <label htmlFor="team-contact-name">Name</label>
            <input
              type="text"
              id="team-contact-name"
              value={teamForm.contactName}
              name="name"
              required
              placeholder="Jane Doe"
              autoComplete="name"
              onChange={(e) => handleChange(e, "contactName")}
            />
            {contactNameError && (
              <p className="teams-error" role="alert">
                {contactNameError}
              </p>
            )}
          </div>
          <div className="teams-form-group">
            <label htmlFor="team-size">Team Size</label>
            <select
              id="team-size"
              name="team_size"
              value={teamForm.teamSize}
              required
              onChange={(e) => handleChange(e, "teamSize")}
            >
              <option value="" disabled>
                Select number of seats...
              </option>
              <option value="5-20">5 to 20 seats</option>
              <option value="21-50">21 to 50 seats</option>
              <option value="51-100">51 to 100 seats</option>
              <option value="100+">100+ seats</option>
            </select>
          </div>
          <div className="teams-recaptcha">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.RECAPTCHA_SITE_TOKEN}
            />
            {captchaError && (
              <div className="teams-captcha-error" role="alert">
                <p>{captchaError}</p>
              </div>
            )}
          </div>
          <button
            className={`teams-btn teams-btn-blue ${
              teamForm.saving ? "loading" : ""
            }`}
            disabled={teamForm.saving}
          >
            Get a Quote & Brochure
          </button>
        </form>
      )}
    </div>
  );
};
